import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, UserMinus, UserCheck, Search, AlertTriangle, Copy, Check, ShieldCheck } from "lucide-react";

// Guarded write actions runnable from the ⌘K palette. Each requires picking a target employee
// and a confirm step; the actual API call reuses the existing lifecycle endpoints.
export const ACTION_DEFS = {
  resetPassword: { icon: KeyRound, perm: ["passwordReset"], titleKey: "cmdact.resetPassword",
                   endpoint: (uid) => api.post(`/users/${uid}/reset-password`, { forceChange: true }),
                   destructive: false, doneKey: "cmdact.resetDone", reveal: true },
  disableUser: { icon: UserMinus, perm: ["employees.enableDisable", "employees.manage"], titleKey: "cmdact.disableUser",
                 endpoint: (uid) => api.post(`/users/${uid}/disable`), destructive: true, doneKey: "cmdact.disableDone" },
  enableUser: { icon: UserCheck, perm: ["employees.enableDisable", "employees.manage"], titleKey: "cmdact.enableUser",
                endpoint: (uid) => api.post(`/users/${uid}/enable`), destructive: false, doneKey: "cmdact.enableDone" },
};

export function CommandActionDialog({ actionKey, onClose }) {
  const { t } = useLang();
  const { user } = useAuth();
  const def = actionKey ? ACTION_DEFS[actionKey] : null;
  const [users, setUsers] = useState([]);
  const [privileged, setPrivileged] = useState(new Set());
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(null);   // { password } shown once after a reset
  const [copied, setCopied] = useState(false);
  const [guardFailed, setGuardFailed] = useState(false);

  useEffect(() => {
    if (!actionKey) { setUsers([]); setQ(""); setPicked(null); setRevealed(null); setCopied(false); setGuardFailed(false); return; }
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    // Directory-role holders are excluded from the picker (privileged-account safety net). If this
    // check fails we FAIL CLOSED — block the picker rather than risk listing privileged accounts.
    api.get("/privileged-user-ids")
      .then((r) => setPrivileged(new Set(r.data?.ids || [])))
      .catch(() => setGuardFailed(true));
  }, [actionKey]);

  // Exclude the signed-in admin + any directory-role admin so an operator can never reset/disable
  // a privileged (or their own) account from the palette. The backend also protects the global admin.
  const meEmail = (user?.email || "").toLowerCase();
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const pool = users.filter((u) => !u.isShared && !u.isGuest && !u.isGlobalAdmin
      && !privileged.has(u.id)
      && (u.userPrincipalName || "").toLowerCase() !== meEmail
      && (u.mail || "").toLowerCase() !== meEmail);
    if (!term) return pool.slice(0, 8);
    return pool.filter((u) =>
      (u.displayName || "").toLowerCase().includes(term) ||
      (u.userPrincipalName || "").toLowerCase().includes(term) ||
      (u.mail || "").toLowerCase().includes(term)).slice(0, 8);
  }, [users, q, meEmail, privileged]);

  const run = async () => {
    if (!picked || !def) return;
    setBusy(true);
    try {
      const res = await def.endpoint(picked.id);
      let wf = res?.data?.workflow;
      const name = picked.displayName || picked.userPrincipalName;
      // On-prem/hybrid resets are dispatched to the agent asynchronously (status starts 'running').
      // Poll the workflow to a terminal state so we NEVER reveal a password that the domain rejected.
      const nonTerminal = (w) => w?.status === "running" || w?.status === "pending";
      if (def.reveal && wf?.id && nonTerminal(wf)) {
        for (let i = 0; i < 8 && nonTerminal(wf); i++) {
          await new Promise((r) => setTimeout(r, 1500));
          try { const d = (await api.get(`/workflows/${wf.id}`)).data; wf = d?.workflow || d; } catch { /* keep polling */ }
        }
      }
      const steps = wf?.steps || [];
      const failedStep = steps.find((s) => s.status === "error");
      if (wf?.status === "error" || failedStep) {
        // Rejected (e.g. on-prem AD complexity/history) — surface it, never reveal an unset password.
        toast.error(failedStep?.message || t("cmdact.actionFailed"));
        return;
      }
      if (def.reveal && nonTerminal(wf)) {
        // Still not confirmed after polling — direct the admin to the Action Log instead of revealing.
        toast.info(t("cmdact.dispatched").replace("{name}", name));
        onClose();
        return;
      }
      const pw = res?.data?.password;
      if (def.reveal && pw) {
        setRevealed({ password: pw });   // confirmed applied — reveal once, right here
        toast.success(t(def.doneKey).replace("{name}", name));
      } else {
        toast.success(t(def.doneKey).replace("{name}", name));
        onClose();
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const copyPw = async () => {
    try {
      await navigator.clipboard.writeText(revealed.password);
      setCopied(true);
      toast.success(t("cmdact.pwCopied"));
    } catch { toast.error(t("cmdact.pwCopyFail")); }
  };

  const Icon = def?.icon || KeyRound;
  return (
    <Dialog open={!!actionKey} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="command-action-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Icon size={17} className="text-primary" /> {def ? t(def.titleKey) : ""}</DialogTitle>
          <DialogDescription>{revealed ? t("cmdact.pwRevealNote") : picked ? t("cmdact.confirmPrompt") : t("cmdact.pickPrompt")}</DialogDescription>
        </DialogHeader>

        {revealed ? (
          <div className="space-y-3" data-testid="command-action-reveal">
            <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm">
              <span className="font-semibold text-zinc-900">{picked?.displayName || picked?.userPrincipalName}</span>
              <span className="block font-mono text-[11px] text-zinc-500">{picked?.userPrincipalName}</span>
            </div>
            <div className="flex items-center gap-2">
              <code data-testid="command-action-password" className="flex-1 select-all rounded-sm border border-zinc-300 bg-white px-3 py-2 font-mono text-sm tracking-wide text-zinc-900">{revealed.password}</code>
              <Button onClick={copyPw} data-testid="command-action-copy-password"
                className="h-9 shrink-0 rounded-sm bg-primary text-primary-foreground hover:bg-[#002480]">
                {copied ? <Check size={15} className="mr-1.5" /> : <Copy size={15} className="mr-1.5" />} {copied ? t("cmdact.pwCopied") : t("cmdact.copy")}
              </Button>
            </div>
            <p className="flex items-start gap-1.5 rounded-sm border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-700"><ShieldCheck size={13} className="mt-0.5 shrink-0" /> {t("cmdact.pwRevealWarn")}</p>
          </div>
        ) : !picked ? (
          <div className="space-y-2">
            {guardFailed ? (
              <p className="flex items-start gap-1.5 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="command-action-guard-failed">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {t("cmdact.guardFailed")}
              </p>
            ) : (
            <>
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("cmdact.searchPh")}
                data-testid="command-action-search" className="rounded-sm border-zinc-300 pl-8" />
            </div>
            <div className="max-h-64 divide-y divide-zinc-100 overflow-y-auto border border-zinc-200" data-testid="command-action-userlist">
              {filtered.length === 0 && <div className="px-3 py-4 text-center text-xs text-zinc-400">{t("cmdact.noUsers")}</div>}
              {filtered.map((u) => (
                <button key={u.id} type="button" onClick={() => setPicked(u)} data-testid={`command-action-user-${u.id}`}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50">
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-zinc-800">{u.displayName || u.userPrincipalName}</span>
                    <span className="block truncate font-mono text-[11px] text-zinc-400">{u.userPrincipalName}</span>
                  </span>
                  <span className={`shrink-0 rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase ${u.accountEnabled === false ? "border-zinc-300 bg-zinc-50 text-zinc-500" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                    {u.accountEnabled === false ? t("cmdact.stDisabled") : t("cmdact.stActive")}
                  </span>
                </button>
              ))}
            </div>
            </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm" data-testid="command-action-target">
              <span className="font-semibold text-zinc-900">{picked.displayName || picked.userPrincipalName}</span>
              <span className="block font-mono text-[11px] text-zinc-500">{picked.userPrincipalName}</span>
            </div>
            {def?.destructive && (
              <p className="flex items-start gap-1.5 rounded-sm border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-700">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {t("cmdact.destructiveWarn")}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {revealed ? (
            <Button onClick={onClose} data-testid="command-action-done" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#002480]">{t("cmdact.done")}</Button>
          ) : (
            <>
              {picked && <Button variant="outline" onClick={() => setPicked(null)} data-testid="command-action-back" className="rounded-sm border-zinc-300">{t("cmdact.back")}</Button>}
              <Button variant="outline" onClick={onClose} data-testid="command-action-cancel" className="rounded-sm border-zinc-300">{t("jobs.cancel")}</Button>
              <Button onClick={run} disabled={!picked || busy} data-testid="command-action-confirm"
                className={`rounded-sm ${def?.destructive ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-[#002480]"} text-primary-foreground`}>
                {busy ? t("cmdact.checking") : (def ? t(def.titleKey) : "")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
